// Das Kolonie-Panel (Akt III) — reine Funktionen, die HTML und Schlüssel liefern.
//
// Dieselbe Teilung wie bei Hilfe, Fundbuch und Konverter: was eine Regel oder
// einen Text trägt, liegt hier, ohne DOM und ohne Phaser — prüfbar in Node.
// Die Verdrahtung (Knöpfe, laufende Zahlen) steht in `ui/colonypanel.ts`.
//
// **Jede Liste hat einen Änderungsschlüssel, und er bildet ab, was sie zeigt**
// (`oberflaeche.md`): ein Bit je Kostenposten statt einer Lagerzahl. Die
// Kolonie produziert ununterbrochen; ein Schlüssel mit gefloorten Gütern
// ersetzte die Liste mehrmals je Sekunde, und der Ausbau-Knopf würde dem
// Spieler unter dem Finger weggezogen. Die laufenden Zahlen — Lager, Raten,
// Netz, Projektfortschritt — werden je Bild an ihren Platz geschrieben.

import { escapeHtml, fmt } from '../core/format';
import {
  balancing, colonyBuildingById, colonyBuildings, colonyRecipeById, goodById, goods, planetById,
  planets, projectsOf, resource,
} from '../data/balancing';
import type { ColonyBuilding, Planet, Project, StatMap } from '../data/types';
import {
  areaRadius, boosts, buildingAt, buildingCost, buildingFlow, buildingUnlocked, canAfford,
  landLocked, nextProject, placeCost, type ColonyHost, type ColonyState, type ColonyView,
  type Flow,
} from '../sim/colony';
import { depositAt } from '../sim/planets';

/** Die Daten-Attribute der Knöpfe — eine Stelle, an der sie stehen. */
export const COLONY_ATTR = {
  planet: 'data-cplanet',
  place: 'data-cplace',
  upgrade: 'data-cupgrade',
  recipe: 'data-crecipe',
  switch: 'data-cswitch',
  move: 'data-cmove',
  project: 'data-cproject',
  land: 'data-cland',
  course: 'data-ccourse',
  cancel: 'data-ccancel',
} as const;

/** Eine Rate je Sekunde — kleine Zahlen brauchen Nachkommastellen, große nicht. */
export function rateStr(n: number): string {
  const betrag = Math.abs(n);
  const zeichen = n < 0 ? '−' : '+';
  if (betrag < 0.005) return '±0';
  if (betrag < 1) return `${zeichen}${betrag.toFixed(2)}`;
  if (betrag < 10) return `${zeichen}${betrag.toFixed(1)}`;
  return zeichen + fmt(betrag);
}

const ok = (gedeckt: boolean): string => (gedeckt ? '#7ee06a' : '#ff6a5c');

/** Ein Kostenposten mit Farbe nach Deckung — Gut oder Schiffsvorrat. */
function posten(host: ColonyHost, id: string, menge: number): string {
  const gut = goodById(id);
  const vorrat = gut ? host.colony.goods[id] ?? 0 : host.res[id] ?? 0;
  const name = gut?.name ?? resource(id)?.name ?? id;
  return `<span class="cpost" style="color:${ok(vorrat >= menge)}">${fmt(menge)} ${escapeHtml(name)}</span>`;
}

const kostenHtml = (host: ColonyHost, cost: StatMap): string =>
  Object.entries(cost).map(([id, menge]) => posten(host, id, menge)).join(' · ');

/** Ein Bit je Kostenposten — der Teil eines Schlüssels, der sich selten ändert. */
export function costBits(host: ColonyHost, cost: StatMap): string {
  return Object.entries(cost).map(([id, menge]) => {
    const vorrat = goodById(id) ? host.colony.goods[id] ?? 0 : host.res[id] ?? 0;
    return vorrat >= menge ? '1' : '0';
  }).join('');
}

// ── Planeten-Reiter ──────────────────────────────────────────────────────────

/** Was ein Planet für den Spieler gerade ist: besiedelt, anfliegbar oder noch zu. */
export function planetStatus(host: ColonyHost, planet: Planet): 'founded' | 'ready' | 'reach' | 'locked' {
  if (host.colony.planets[planet.id]) return 'founded';
  const grund = landLocked(host, planet);
  if (!grund) return 'ready';
  return grund.startsWith('Flieg') ? 'reach' : 'locked';
}

const STATUS_ZEICHEN: Record<ReturnType<typeof planetStatus>, string> = {
  founded: '✓', ready: '▼', reach: '◎', locked: '🔒',
};

export function planetTabsKey(host: ColonyHost, active: string): string {
  return active + '|' + planets().map((p) => planetStatus(host, p)[0]).join('');
}

export function planetTabsHtml(host: ColonyHost, active: string): string {
  return planets().map((planet) => {
    const status = planetStatus(host, planet);
    return `<button class="subtab ${planet.id === active ? 'active' : ''} cstat-${status}"
      ${COLONY_ATTR.planet}="${planet.id}">${STATUS_ZEICHEN[status]} ${escapeHtml(planet.name)}</button>`;
  }).join('');
}

// ── Ein Planet ohne Kolonie ──────────────────────────────────────────────────

export function planetCardKey(host: ColonyHost, planet: Planet): string {
  return planet.id + '|' + landLocked(host, planet) + '|' + costBits(host, planet.landCost);
}

/**
 * Die Karte eines noch nicht besiedelten Planeten.
 *
 * Der Grund einer Sperre kommt aus **derselben** Auswertung wie die Sperre
 * (`landLocked`), und der Preis steht da, **bevor** er gezahlt werden kann —
 * ein unsichtbarer Preis machte aus dem Kauf ein Ereignis (`GAME-001`).
 */
export function planetCardHtml(host: ColonyHost, planet: Planet): string {
  const grund = landLocked(host, planet);
  const bereit = grund === '';
  const gedeckt = canAfford(host, planet.landCost);
  return `<div class="booster cplanet">
    <img class="bi" src="" data-icon="${planet.icon}" alt="" width="34" height="34">
    <div class="btxt">
      <div class="bname">${escapeHtml(planet.name)}</div>
      <div class="bdesc">${escapeHtml(planet.desc)}</div>
      <div class="bdesc cline">Entfernung von der Kapsel: ${fmt(planet.dist)} m</div>
      <div class="bdesc cline">Landung: ${kostenHtml(host, planet.landCost)}</div>
      <div class="bdesc cline">Startausrüstung: ${kostenHtml(host, planet.kit)}</div>
      <div class="bdesc cline ${bereit ? 'cready' : 'clock'}">${bereit
        ? 'In der Umlaufbahn — bereit zur Landung.'
        : escapeHtml(grund)}</div>
      <div class="cbuttons">
        <button ${COLONY_ATTR.course}="${planet.id}">Kurs setzen</button>
        <button ${COLONY_ATTR.land}="${planet.id}" ${bereit && gedeckt ? '' : 'disabled'}>Landen</button>
      </div>
    </div>
  </div>`;
}

/**
 * Der Text des Landedialogs: was es kostet, was mitkommt, was danach gilt.
 *
 * Der Preis steht da, **bevor** gezahlt wird (`GAME-001`). Ist die Landung
 * gesperrt, sagt der Dialog warum — aus derselben Auswertung wie die Sperre.
 */
export function landingHtml(host: ColonyHost, planet: Planet): string {
  const grund = landLocked(host, planet);
  const gedeckt = canAfford(host, planet.landCost);
  return `<p style="line-height:1.6">${escapeHtml(planet.desc)}</p>
    <p class="cline">Landung: ${kostenHtml(host, planet.landCost)}</p>
    <p class="cline">Die Kapsel bringt mit: ${kostenHtml(host, planet.kit)}</p>
    <p class="cline">Die Kolonie arbeitet, solange du spielst — auch während du im Feld fliegst.</p>
    ${grund ? `<p class="cline clock">${escapeHtml(grund)}</p>`
    : gedeckt ? '' : '<p class="cline clock">Die Kosten sind nicht gedeckt.</p>'}`;
}

// ── Bauplan ──────────────────────────────────────────────────────────────────

const ORT: Record<string, string> = {
  deposit: 'auf einem Rohstoffvorkommen',
  heat: 'auf einer Wärmequelle',
  '': 'auf jeder freien Kachel',
};

/** Die baubaren Anlagen — die Kapsel setzt die Landung, nicht der Bauplan. */
const baubar = (): ColonyBuilding[] => colonyBuildings().filter((entry) => entry.role !== 'hq');

/** Gedeckt-Bits und der erste Preisposten — er steigt mit jeder gebauten Anlage. */
function preisSchluessel(host: ColonyHost, planetId: string, kind: ColonyBuilding): string {
  const preis = placeCost(host.colony, planetId, kind);
  return costBits(host, preis) + preis[Object.keys(kind.cost)[0]];
}

export function buildMenuKey(host: ColonyHost, view: ColonyView): string {
  return view.planet + '|' + view.placing + '|' + baubar().map((kind) =>
    buildingUnlocked(host.colony, kind) ? preisSchluessel(host, view.planet, kind) : 'x',
  ).join(',');
}

/**
 * Der Bauplan: jede Anlage mit Preis, Ort und Energie.
 *
 * **Gesperrte werden verdeckt, nicht ausgeblendet** — dieselbe Regel wie im
 * Technologiebaum: ein leerer Bereich sieht nach Ende aus, ein Schloss nach
 * Ziel. Der Preis der nächsten Anlage steht im Schlüssel, nicht die
 * Lagermenge: er steigt mit jeder gebauten, und dann muss die Zeile neu.
 */
export function buildMenuHtml(host: ColonyHost, view: ColonyView): string {
  return `<div class="chead">Bauplan</div>` + baubar().map((kind) => {
    if (!buildingUnlocked(host.colony, kind)) {
      const projekt = kind.need ? balancing().projects.find((p) => p.id === kind.need) : undefined;
      return `<div class="cbuild locked">
        <img class="bi" src="" data-icon="lock" alt="" width="34" height="34">
        <div class="btxt"><div class="bname">${escapeHtml(kind.name)}</div>
        <div class="bdesc">🔒 braucht ${escapeHtml(projekt?.name ?? kind.need)}</div></div>
      </div>`;
    }
    const preis = placeCost(host.colony, view.planet, kind);
    const aktiv = view.placing === kind.id;
    const energie = kind.power > 0 ? `+${fmt(kind.power)} Energie` : `${fmt(-kind.power)} Energie`;
    return `<div class="cbuild ${aktiv ? 'active' : ''}">
      <img class="bi" src="" data-icon="${kind.icon}" alt="" width="34" height="34">
      <div class="btxt">
        <div class="bname">${escapeHtml(kind.name)}</div>
        <div class="bdesc">${escapeHtml(kind.desc)}</div>
        <div class="bdesc">${ORT[kind.site] ?? ''} · je Stufe ${energie}</div>
        <div class="bdesc">${kostenHtml(host, preis)}</div>
      </div>
      <button ${COLONY_ATTR.place}="${kind.id}" ${canAfford(host, preis) || aktiv ? '' : 'disabled'}>
        ${aktiv ? 'Fertig' : 'Bauen'}</button>
    </div>`;
  }).join('');
}

// ── Auswahl ──────────────────────────────────────────────────────────────────

export function selectionKey(host: ColonyHost, view: ColonyView, flow: Flow): string {
  // Die Zahl abgeschlossener Projekte steht im Schlüssel: ein Projekt hebt mit
  // seinem Zuschlag die Leistung, die das Auswahlfeld zeigt.
  const projekte = Object.values(host.colony.planets).reduce((summe, o) => summe + o.projects, 0);
  if (!view.selected) return view.planet + '|-|' + view.placing + '|' + projekte;
  const outpost = host.colony.planets[view.planet];
  const anlage = outpost ? buildingAt(outpost, view.selected.x, view.selected.y) : undefined;
  const ort = `${view.planet}|${view.selected.x}:${view.selected.y}|${view.placing}|${view.moving}`;
  if (!anlage) return ort + '|' + areaRadius(host.colony, view.planet);
  const kind = colonyBuildingById(anlage.kind);
  const preis = kind ? buildingCost(kind, anlage.level) : {};
  return `${ort}|${anlage.kind}|${anlage.level}|${anlage.recipe}|${anlage.on}|${projekte}|`
    + `${costBits(host, preis)}|${flow.starved[anlage.recipe] ? 1 : 0}|${Math.round(flow.ratio * 20)}`;
}

/**
 * Was auf der ausgewählten Kachel steht — und was man dort tun kann.
 *
 * Für eine leere Kachel: das Vorkommen und welche Anlage dort passt. Für eine
 * Anlage: ihre Leistung, ihr Band, ihr Schalter und der Ausbau.
 */
export function selectionHtml(host: ColonyHost, view: ColonyView, flow: Flow): string {
  if (view.placing) {
    const kind = colonyBuildingById(view.placing);
    return `<div class="cselect"><div class="bname">Bauplan: ${escapeHtml(kind?.name ?? '')}</div>
      <div class="bdesc">Tippe auf eine markierte Kachel. ${ORT[kind?.site ?? ''] ?? ''}.</div>
      <button ${COLONY_ATTR.cancel}>Bauplan weglegen</button></div>`;
  }
  if (!view.selected) {
    return '<div class="cselect"><div class="bdesc">Tippe auf eine Kachel, um sie anzusehen — oder wähle unten eine Anlage zum Bauen.</div></div>';
  }
  const { x, y } = view.selected;
  const outpost = host.colony.planets[view.planet];
  const anlage = outpost ? buildingAt(outpost, x, y) : undefined;
  if (!anlage) return kachelHtml(host, view);
  const kind = colonyBuildingById(anlage.kind);
  if (!kind) return '';
  if (view.moving) {
    return `<div class="cselect"><div class="bname">${escapeHtml(kind.name)} versetzen</div>
      <div class="bdesc">Tippe auf die neue Kachel. Die Stufe bleibt, es kostet nichts.</div>
      <button ${COLONY_ATTR.cancel}>Abbrechen</button></div>`;
  }
  const leistung = buildingFlow(view.planet, anlage, boosts(host.colony), flow.ratio);
  const preis = buildingCost(kind, anlage.level);
  const voll = anlage.level >= kind.maxLevel;
  const hungert = Boolean(anlage.recipe && flow.starved[anlage.recipe]);
  const rezepte = kind.recipes.length > 1 ? rezeptKnoepfe(kind, anlage.recipe) : '';
  return `<div class="cselect">
    <div class="bname">${escapeHtml(kind.name)} · Stufe ${anlage.level}/${kind.maxLevel}${anlage.on ? '' : ' · angehalten'}</div>
    <div class="bdesc">${escapeHtml(kind.desc)}</div>
    ${flussHtml(leistung)}
    ${hungert ? '<div class="bdesc clock">Es fehlt ein Eingang — die Halle läuft nur teilweise.</div>' : ''}
    ${rezepte}
    ${voll ? '<div class="bdesc">Voll ausgebaut.</div>' : `<div class="bdesc">Nächste Stufe: ${kostenHtml(host, preis)}</div>`}
    <div class="cbuttons">
      ${voll ? '' : ausbauKnoepfe(canAfford(host, preis))}
      ${kind.role === 'hq' ? '' : `<button ${COLONY_ATTR.switch}="${anlage.on ? 'off' : 'on'}">${anlage.on ? 'Anhalten' : 'Anwerfen'}</button>
      <button ${COLONY_ATTR.move}>Versetzen</button>`}
    </div>
  </div>`;
}

/** Die Rezeptwahl einer Halle — das laufende ist gedrückt und gesperrt. */
function rezeptKnoepfe(kind: ColonyBuilding, aktiv: string): string {
  return `<div class="cbuttons">${kind.recipes.map((id) => {
    const rezept = colonyRecipeById(id);
    return `<button ${COLONY_ATTR.recipe}="${id}" ${id === aktiv ? 'class="on" disabled' : ''}>
      ${escapeHtml(rezept?.name ?? id)}</button>`;
  }).join('')}</div>`;
}

/** Ausbauen einmal, zehnmal, so oft es reicht — gesperrt, solange nicht einmal eine Stufe gedeckt ist. */
function ausbauKnoepfe(gedeckt: boolean): string {
  const sperre = gedeckt ? '' : 'disabled';
  return `<button ${COLONY_ATTR.upgrade}="1" ${sperre}>Ausbauen</button>
      <button ${COLONY_ATTR.upgrade}="10" ${sperre}>×10</button>
      <button ${COLONY_ATTR.upgrade}="max" ${sperre}>Max</button>`;
}

function flussHtml(leistung: { made: StatMap; used: StatMap; power: number }): string {
  const teile = (karte: StatMap, zeichen: string): string => Object.entries(karte)
    .map(([gut, menge]) => `${zeichen}${fmtRate(menge)} ${escapeHtml(goodById(gut)?.name ?? gut)}`)
    .join(' · ');
  const zeilen = [
    teile(leistung.made, '+'),
    teile(leistung.used, '−'),
    leistung.power > 0 ? `+${fmt(leistung.power)} Energie` : leistung.power < 0 ? `${fmt(-leistung.power)} Energie Bedarf` : '',
  ].filter(Boolean);
  return zeilen.length ? `<div class="bdesc cline">je Sekunde: ${zeilen.join(' · ')}</div>` : '';
}

const fmtRate = (n: number): string => (n < 1 ? n.toFixed(2) : n < 10 ? n.toFixed(1) : fmt(n));

function kachelHtml(host: ColonyHost, view: ColonyView): string {
  const { x, y } = view.selected ?? { x: -1, y: -1 };
  const c = balancing().colony;
  const vorkommen = depositAt(view.planet, x, y);
  const mitte = { x: Math.floor(c.gridW / 2), y: Math.floor(c.gridH / 2) };
  const radius = areaRadius(host.colony, view.planet);
  const drinnen = Math.abs(x - mitte.x) <= radius && Math.abs(y - mitte.y) <= radius;
  const art = vorkommen
    ? vorkommen.kind === c.depositHeat
      ? `${c.depositHeatName} · Ergiebigkeit ${vorkommen.rich} — hier passt ein Geothermiekraftwerk.`
      : `${goodById(vorkommen.kind)?.name ?? vorkommen.kind} · Ergiebigkeit ${vorkommen.rich} (×${c.richness[vorkommen.rich - 1]}) — hier passt ein Förderturm.`
    : 'Blankes Gestein — hier passt jede Halle.';
  return `<div class="cselect">
    <div class="bname">Kachel ${x + 1}·${y + 1}</div>
    <div class="bdesc">${escapeHtml(art)}</div>
    ${drinnen ? '' : '<div class="bdesc clock">Außerhalb des erschlossenen Gebiets — ein Projekt weitet es.</div>'}
  </div>`;
}

// ── Projekt ──────────────────────────────────────────────────────────────────

/** Was ein Projekt bewirkt, in Worten — aus der Tabelle, nicht abgetippt. */
export function projectEffects(project: Project): string[] {
  const out: string[] = [];
  if (project.area) out.push(`Gebiet +${project.area} Kacheln in jede Richtung`);
  for (const [art, zuschlag] of Object.entries(project.boost)) {
    out.push(`${colonyBuildingById(art)?.name ?? art} +${Math.round(zuschlag * 100)} % Leistung`);
  }
  for (const [stat, zuschlag] of Object.entries(project.perk)) {
    out.push(`Schiff: ${statName(stat)} +${Math.round(zuschlag * 100)} %`);
  }
  for (const kind of colonyBuildings()) {
    if (kind.need === project.id) out.push(`schaltet ${kind.name} frei`);
  }
  for (const planet of planets()) {
    if (planet.need === project.id) out.push(`öffnet den Flug nach ${planet.name}`);
  }
  if (project.final) out.push('vollendet Akt III');
  return out;
}

const STAT_NAMEN: Record<string, string> = {
  yieldMul: 'Ausbeute', globalDamage: 'Schaden', xpMul: 'Erfahrung', dockRegen: 'Ladetempo',
  warpMul: 'Lichtflug',
};

const statName = (stat: string): string => STAT_NAMEN[stat] ?? stat;

export function projectKey(host: ColonyHost, planetId: string): string {
  const projekt = nextProject(host.colony, planetId);
  return planetId + '|' + (projekt ? projekt.id + '|' + costBits(host, projekt.cost) : 'fertig');
}

/**
 * Das nächste Projekt des Planeten mit seinen Posten.
 *
 * Die Balken je Posten sind **laufende Zahlen** und stehen deshalb nicht im
 * Schlüssel; sie bekommen ein `data-cbar` und werden je Bild nachgeschrieben.
 */
export function projectHtml(host: ColonyHost, planetId: string): string {
  const projekt = nextProject(host.colony, planetId);
  const reihe = projectsOf(planetId);
  const planet = planetById(planetId);
  if (!projekt) {
    return `<div class="chead">Projekte</div><div class="quest done">
      <div class="quest-name">Alle ${reihe.length} Projekte auf ${escapeHtml(planet?.name ?? planetId)} sind abgeschlossen.</div></div>`;
  }
  const nummer = reihe.indexOf(projekt) + 1;
  const gedeckt = canAfford(host, projekt.cost);
  const balken = Object.entries(projekt.cost).map(([id, menge]) => `<div class="cbar-row">
      <span class="cbar-name">${escapeHtml(goodById(id)?.name ?? resource(id)?.name ?? id)}</span>
      <span class="cbar-num" data-cnum="${id}">0 / ${fmt(menge)}</span>
      <div class="quest-bar"><div data-cbar="${id}" data-need="${menge}" style="width:0%"></div></div>
    </div>`).join('');
  return `<div class="chead">Projekt ${nummer}/${reihe.length}</div>
    <div class="quest ${gedeckt ? 'claimable' : ''}">
      <div class="quest-top"><span class="quest-name">${escapeHtml(projekt.name)}</span></div>
      <div class="quest-sub">${escapeHtml(projekt.desc)}</div>
      <div class="quest-sub">⮕ ${projectEffects(projekt).map(escapeHtml).join(' · ')}</div>
      ${balken}
      <button ${COLONY_ATTR.project}="${planetId}" ${gedeckt ? '' : 'disabled'}>Abschließen</button>
    </div>`;
}

// ── Lager ────────────────────────────────────────────────────────────────────

/** Welche Güter im Lager stehen: alles mit Bestand oder Bewegung. */
export function shownGoodIds(colony: ColonyState, flow: Flow): string[] {
  return goods().filter((gut) =>
    (colony.goods[gut.id] ?? 0) > 0.0001 || (flow.made[gut.id] ?? 0) > 0 || (flow.used[gut.id] ?? 0) > 0,
  ).map((gut) => gut.id);
}

export const goodsKey = (ids: string[]): string => ids.join(',');

/** Das Lager — Name, Bestand, Rate. Die Zahlen schreibt die Verdrahtung je Bild. */
export function goodsHtml(ids: string[]): string {
  if (ids.length === 0) return '<div class="chead">Lager</div><div class="bdesc">Noch leer.</div>';
  return '<div class="chead">Lager</div><div class="cgoods">' + ids.map((id) => {
    const gut = goodById(id);
    return `<div class="cgood" title="${escapeHtml(gut?.desc ?? '')}">
      <img class="ci" src="" data-icon="${gut?.icon ?? 'lock'}" alt="" width="18" height="18">
      <span class="cgname">${escapeHtml(gut?.name ?? id)}</span>
      <b data-cgood="${id}">0</b>
      <span class="cgrate" data-crate="${id}">±0</span>
    </div>`;
  }).join('') + '</div>';
}

/** Die Netzzeile — Erzeugung gegen Bedarf, als Text. */
export function powerText(flow: Flow): string {
  const deckung = Math.round(flow.ratio * 100);
  return `Netz ${fmt(flow.supply)} / ${fmt(flow.demand)} Energie`
    + (deckung < 100 ? ` — nur ${deckung} % gedeckt, alles läuft langsamer` : '');
}
